/* Sélecteur d'offres — bascule des paliers, champ nom conditionnel, lignes de panier.

   Le panier reçoit de VRAIES lignes (une par produit), comme aujourd'hui : rien n'est fusionné,
   donc la facture fournisseur et le COGS par produit ne bougent pas.

   ponytail: on écrit les `items[n][…]` dans le trou que le thème prévoit déjà
   (`.product-form__variants`) et on désactive `[name=id]` — c'est exactement ce que fait
   `bundle-deals` de Shrine. Si Shopify recevait les deux, il ajouterait la ligne en double.
*/
class BundleSelector extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.form);
    if (!this.form) return;
    this.idInput = this.form.querySelector('[name="id"]');
    this.linesHost = this.form.querySelector('.product-form__variants');
    this.offers = Array.from(this.querySelectorAll('[data-offer]'));

    this.mainColours = {};
    this.mainImages = {};
    const mapTag = this.querySelector('[data-main-colour-map]');
    if (mapTag) {
      try { this.mainImages = JSON.parse(mapTag.textContent); } catch (e) { this.mainImages = {}; }
    }
    /* id -> couleur pour TOUTES les variantes. La table des images n'en garde qu'une par
       couleur : s'en servir ici ne reconnaissait la couleur que sur la première taille. */
    const vcTag = this.querySelector('[data-variant-colours]');
    if (vcTag) {
      try { this.mainColours = JSON.parse(vcTag.textContent); } catch (e) { this.mainColours = {}; }
    }

    this.offers.forEach((offer) => {
      offer.querySelector('[data-offer-head]').addEventListener('click', () => this.select(offer.dataset.offer));
      const toggle = offer.querySelector('[data-addon-toggle]');
      if (toggle) toggle.addEventListener('change', () => this.sync());
    });

    /* Les radios de taille et de couleur vivent dans <variant-selects>, PAS dans le <form> :
       elles n'y sont rattachées que par leur attribut `form`. Un écouteur posé sur le
       formulaire ne les voit donc jamais — il faut écouter le document. */
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t && t.matches && t.matches('input[type="radio"], select')) {
        setTimeout(() => this.sync(), 0);
      }
    }, true);
    document.addEventListener('variant:changed', () => setTimeout(() => this.sync(), 0));

    // Capture sur le document : ce handler passe AVANT celui de <product-form>,
    // donc les lignes sont posées avant que le thème ne sérialise le formulaire.
    document.addEventListener('submit', (e) => {
      if (e.target === this.form) this.onSubmit(e);
    }, true);

    this.select(this.dataset.selected || '1');
  }

  get current() {
    return this.offers.find((o) => o.classList.contains('is-selected')) || this.offers[0];
  }

  select(value) {
    this.offers.forEach((offer) => {
      const on = offer.dataset.offer === value;
      offer.classList.toggle('is-selected', on);
      offer.querySelector('[data-offer-head]').setAttribute('aria-checked', String(on));
    });
    this.sync();
  }

  /* Le prix du harnais suit la variante choisie : la ligne « main » du kit doit suivre aussi. */
  mainVariantId() {
    return this.idInput && this.idInput.value ? this.idInput.value : null;
  }

  currentColour() {
    return this.mainColours[this.mainVariantId()] || null;
  }

  /* La couleur choisie repeint toutes les vignettes, et fait suivre la laisse assortie. */
  applyColour() {
    const colour = this.currentColour();
    if (!colour) return;

    const hero = this.mainImages && this.mainImages[colour];
    if (hero) {
      this.querySelectorAll('img[data-follows-colour]').forEach((img) => {
        img.src = hero.img;
        img.removeAttribute('srcset');
      });
    }

    this.querySelectorAll('[data-colour-map]').forEach((row) => {
      let map;
      try { map = JSON.parse(row.dataset.colourMap); } catch (e) { return; }
      const hit = map[colour];
      if (!hit) return;                       // pas de couleur assortie : on ne touche à rien
      /* ponytail: la ligne du harnais est pilotee par la TAILLE choisie (voir sync), pas par
         cette table qui ne garde qu'une variante par couleur — la premiere taille, XS. Sans
         ce garde-fou applyColour repasse derriere sync et renvoie tout le monde en XS. */
      if (!row.hasAttribute('data-line-main')) row.dataset.lineVariant = hit.id;
      const img = row.querySelector('img.bsel__item-tile');
      if (img && hit.img) { img.src = hit.img; img.removeAttribute('srcset'); }
    });
  }

  sync() {
    const offer = this.current;
    if (!offer) return;

    // Champ nom : actif seulement s'il est visible dans l'offre choisie.
    this.querySelectorAll('[data-name-input]').forEach((input) => {
      const own = input.closest('[data-offer]') === offer;
      const addon = input.closest('[data-addon-field]');
      const toggle = offer.querySelector('[data-addon-toggle]');
      const live = own && (!addon || (toggle && toggle.checked));
      input.disabled = !live;
      if (addon) addon.hidden = !live;
      if (!live) this.clearError(input);
    });

    // Ligne principale du kit = la variante réellement sélectionnée.
    const mainId = this.mainVariantId();
    this.querySelectorAll('[data-line-main]').forEach((row) => {
      if (mainId) row.dataset.lineVariant = mainId;
      const img = row.querySelector('img.bsel__item-tile');
      const hero = this.mainImages && this.mainImages[this.currentColour()];
      if (img && hero) { img.src = hero.img; img.removeAttribute('srcset'); }
    });

    this.applyColour();
  }

  /* Toutes les lignes de l'offre : la variante principale, les produits listés, l'add-on. */
  lines(offer) {
    const ids = [];
    const mainId = this.mainVariantId();
    const rows = offer.querySelectorAll('[data-line-variant]');
    if (rows.length) {
      rows.forEach((row) => ids.push(row.dataset.lineVariant));
    } else if (mainId) {
      ids.push(mainId);
    }

    const toggle = offer.querySelector('[data-addon-toggle]');
    if (toggle && toggle.checked && toggle.dataset.addonVariant) ids.push(toggle.dataset.addonVariant);

    const included = offer.querySelector('[data-included-field]');
    if (included && included.dataset.includedVariant) ids.push(included.dataset.includedVariant);

    // Une targhetta listée dans le contenu ET portée par `o2_addon_product` ne doit
    // partir qu'UNE fois : sinon le client la paie en double.
    return ids.filter((id, i) => id && ids.indexOf(id) === i);
  }

  /* Les cadeaux ne sont pas facturés : ils voyagent en propriétés, comme chez ParoPet. */
  gifts(offer) {
    return Array.from(offer.querySelectorAll('[data-gift]')).map((el) => el.dataset.gift);
  }

  showError(input) {
    input.classList.add('is-error');
    input.setAttribute('aria-invalid', 'true');
    const err = input.parentElement.querySelector('[data-name-error]');
    if (err) err.hidden = false;
    input.focus();
  }

  clearError(input) {
    input.classList.remove('is-error');
    input.removeAttribute('aria-invalid');
    const err = input.parentElement.querySelector('[data-name-error]');
    if (err) err.hidden = true;
  }

  /* On envoie nous-mêmes.

     Écrire les `items[n][…]` dans `.product-form__variants` ne marche pas : <product-form>
     réécrit ce div après nous — c'est son propre mécanisme pour les bundles du thème. Le
     formulaire partait donc sans aucun `items`, d'où le « Required parameter missing or
     invalid: items ». On coupe la soumission native et on poste le JSON directement, comme
     le fait ParoPet. */
  /* Un message dans l'encart d'erreur du thème, plutôt qu'une alerte navigateur. */
  showFormError(text) {
    const box = this.form.querySelector('.product-form__error-message-wrapper');
    const msg = this.form.querySelector('.product-form__error-message');
    if (box && msg) { msg.textContent = text; box.hidden = false; }
    else window.alert(text);
  }

  async onSubmit(event) {
    const offer = this.current;
    if (!offer) return;

    /* Le bouton ne porte qu'un `aria-disabled` : il reste cliquable. Sans ce verrou, un
       double-clic poste deux fois et le client reçoit le kit en double. */
    if (this.submitting) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    /* Echouer FERME. Si la variante choisie n'est pas connue, on bloque au lieu d'envoyer
       celle que Liquid avait posee au chargement — c'est ce glissement qui a expedie
       26 commandes en XS le 2026-08-23. */
    if (!this.mainVariantId()) {
      event.preventDefault();
      event.stopPropagation();
      /* Texte traduit : locales/*.json products.product.bundle_selector.choose_variant_error,
         pose par snippets/bundle-selector.liquid dans data-error-variant (2026-10-11). */
      this.showFormError(this.dataset.errorVariant);
      return;
    }

    const name = offer.querySelector('[data-name-input]:not([disabled])');
    if (name) {
      name.value = name.value.trim();
      if (!name.value) {
        event.preventDefault();
        event.stopPropagation();
        this.showError(name);
        return;
      }
      this.clearError(name);
    }

    const ids = this.lines(offer);
    if (!ids.length) return;   // rien à envoyer : on laisse le thème faire

    event.preventDefault();
    event.stopPropagation();
    this.submitting = true;

    /* `shared` voyage sur toutes les lignes du groupe, `lead` seulement sur la premiere :
       le nom grave se lit une fois, pas six. */
    const shared = {};
    const lead = {};
    if (name) lead[name.name.replace(/^properties\[(.*)\]$/, '$1')] = name.value;
    this.gifts(offer).forEach((label) => { shared['🎁 ' + label] = '✓'; });
    if (ids.length > 1) {
      shared._Kit = 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      lead._KitLead = '1';
    }

    const button = this.form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-disabled', 'true');
    const spinner = button && button.querySelector('.loading-overlay__spinner, .loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          /* Shopify remonte les lignes d'un ajout groupe dans l'ordre inverse : on envoie
             a l'envers pour que le harnais s'affiche en tete du kit, pas l'e-book. */
          items: ids.map((id, i) => ({
            id: Number(id),
            quantity: 1,
            properties: i === 0 ? Object.assign({}, shared, lead) : Object.assign({}, shared),
          })).reverse(),
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || this.dataset.errorAdd);

      const sections = data.sections || {};
      Object.keys(sections).forEach((key) => {
        const host = document.getElementById('shopify-section-' + key);
        if (!host) return;
        const parsed = new DOMParser().parseFromString(sections[key], 'text/html');
        const fresh = parsed.getElementById('shopify-section-' + key);
        host.innerHTML = fresh ? fresh.innerHTML : sections[key];
      });

      // Le thème ouvre le tiroir sur ce clic : on réutilise son chemin plutôt que le nôtre.
      const icon = document.querySelector('#cart-icon-bubble');
      if (icon) icon.click();
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (err) {
      this.showFormError(err.message);
    } finally {
      this.submitting = false;
      if (button) button.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }
}

if (!customElements.get('bundle-selector')) customElements.define('bundle-selector', BundleSelector);
